import { initializeApp, getApp, getApps } from 'firebase/app';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  reauthenticateWithPopup,
  reauthenticateWithRedirect,
  linkWithCredential,
  linkWithPopup,
  linkWithRedirect,
  signOut,
  deleteUser,
  reload,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

const projectIdForAuth = String(process.env.REACT_APP_FIREBASE_PROJECT_ID || '').trim();
const configuredAuthDomain = String(process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '').trim();
/**
 * OAuth popups/iframes load `https://<authDomain>/__/auth/handler`, which Firebase serves on
 * `{projectId}.firebaseapp.com`. A custom marketing domain on Cloud Run / static SPA often serves
 * `index.html` for that path instead, which breaks sign-in (nested app + "popup closed by user").
 */
const firebaseHostedAuthDomain = projectIdForAuth ? `${projectIdForAuth}.firebaseapp.com` : '';
const resolvedAuthDomain = firebaseHostedAuthDomain || configuredAuthDomain;

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: resolvedAuthDomain,
  projectId: projectIdForAuth,
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
};

if (typeof window !== 'undefined' && firebaseHostedAuthDomain) {
  const handler = `https://${firebaseHostedAuthDomain}/__/auth/handler`;
  const bits = [
    `OAuth authDomain="${firebaseHostedAuthDomain}" (browser may visit this host during Apple sign-in — expected).`,
    `Web Apple uses redirect (not popup).`,
    `Apple Developer → Identifiers → Services ID → Sign in with Apple → Configure → Web:`,
    `  • Return URLs must include exactly: ${handler}`,
    `  • Domains and Subdomains: ${firebaseHostedAuthDomain}`,
    `Firebase Console → Authentication → Settings → Authorized domains: include your SPA host (e.g. pro.re-build-me.com) and ${firebaseHostedAuthDomain}.`,
  ];
  if (configuredAuthDomain && configuredAuthDomain !== firebaseHostedAuthDomain) {
    bits.push(
      `Build uses REACT_APP_FIREBASE_AUTH_DOMAIN="${configuredAuthDomain}" for marketing; OAuth still uses ${firebaseHostedAuthDomain} above.`,
    );
  }
  console.info(`[firebase]\n${bits.join('\n')}`);
}

const requiredConfigKeys = ['apiKey', 'authDomain', 'projectId', 'appId'];
const missingConfigKeys = requiredConfigKeys.filter((key) => !String(firebaseConfig[key] || '').trim());
const hasConfig = missingConfigKeys.length === 0;

/** OAuth redirect / Apple Return URL host — always `{projectId}.firebaseapp.com` when project id is set (see resolvedAuthDomain). */
export const getFirebaseAppleOAuthHandlerUrl = () => {
  if (firebaseHostedAuthDomain) {
    return `https://${firebaseHostedAuthDomain}/__/auth/handler`;
  }
  const host = String(configuredAuthDomain || '').replace(/^https?:\/\//, '').replace(/\/+$/, '');
  return host ? `https://${host}/__/auth/handler` : '';
};

export const getFirebaseConfigDiagnostics = () => ({
  hasConfig,
  missingConfigKeys,
  /** Paste into Apple Developer → Services ID → Sign in with Apple → Web → Return URLs (exact match). */
  appleOAuthHandlerUrl: getFirebaseAppleOAuthHandlerUrl(),
  /** Host used as Firebase `authDomain` in the web SDK (OAuth iframe/redirect host). */
  firebaseAuthHost: resolvedAuthDomain,
});

const formatFirebaseError = (error) => {
  if (!error) return 'Unknown error';
  const code = error.code ? String(error.code) : '';
  const message = error.message ? String(error.message) : '';
  if (code && message) return `${code}: ${message}`;
  if (message) return message;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

export const firebaseApp = hasConfig ? (getApps().length ? getApp() : initializeApp(firebaseConfig)) : null;

/** Session flags for full-window OAuth (cleared in consumeWebAuthRedirectResult). */
export const APPLE_WEB_SIGNIN_PENDING_KEY = 'rbm_apple_web_signin_pending';
export const GOOGLE_WEB_SIGNIN_PENDING_KEY = 'rbm_google_web_signin_pending';

const createFirebaseAuth = () => {
  if (!firebaseApp) return null;
  const auth = getAuth(firebaseApp);
  if (typeof window !== 'undefined' && !Capacitor.isNativePlatform()) {
    // Keep web sessions across refresh/reopen. Use getAuth() default popup resolver to avoid auth/argument-error.
    void setPersistence(auth, browserLocalPersistence).catch((e) => {
      console.warn('[firebase] setPersistence failed:', e?.message || e);
    });
  }
  return auth;
};

export const firebaseAuth = createFirebaseAuth();

/**
 * Capacitor can surface a signed-in user via `subscribeToAuth` before `firebaseAuth.currentUser`
 * is hydrated. Firestore entitlements use `users/{uid}` — any code that reads tier/cache by uid
 * must use the same id (e.g. `getCurrentFirebaseUid`) until the web session catches up.
 */
let lastKnownAuthUid = '';
export const setLastKnownAuthUid = (uid) => {
  lastKnownAuthUid = String(uid || '').trim();
};
export const getCurrentFirebaseUid = () => {
  const web = firebaseAuth?.currentUser?.uid;
  if (web) return web;
  return lastKnownAuthUid;
};

export const googleProvider = new GoogleAuthProvider();
export const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

/** Apple on web: full-window redirect avoids cross-site popup issues that can trigger "use a different Apple account". */
const shouldUseWebAppleRedirect = () => !Capacitor.isNativePlatform();

/**
 * Firebase stores `location.href` as the OAuth continuation URL. When the SPA is served for
 * `/__/auth/handler` (static host fallback), a second `signInWithRedirect` nests `redirectUrl`
 * (firebaseapp.com → custom handler → …) and Apple / Firebase redirect breaks ("different user", stuck UI).
 * Call after `getRedirectResult`, and before starting any web redirect/link/reauth flow.
 */
const ensureWebFirebaseAuthContinuationPathIsRoot = () => {
  if (typeof window === 'undefined' || Capacitor.isNativePlatform()) return;
  const path = String(window.location.pathname || '').replace(/\/+$/, '') || '/';
  if (!/\/__\/auth\/handler$/.test(path)) return;
  try {
    window.history.replaceState(window.history.state, document.title, `${window.location.origin}/`);
  } catch {
    /* ignore */
  }
};

export const consumeWebAuthRedirectResult = async () => {
  if (!firebaseAuth || Capacitor.isNativePlatform()) return null;
  try {
    return await getRedirectResult(firebaseAuth);
  } catch (e) {
    console.warn('getRedirectResult:', formatFirebaseError(e));
    return null;
  } finally {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(APPLE_WEB_SIGNIN_PENDING_KEY);
        sessionStorage.removeItem(GOOGLE_WEB_SIGNIN_PENDING_KEY);
      }
    } catch {
      /* ignore */
    }
    ensureWebFirebaseAuthContinuationPathIsRoot();
  }
};

const isNativeFirebasePluginAvailable = () => (
  Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('FirebaseAuthentication')
);

const isRequiresRecentLoginError = (error) => {
  const text = formatFirebaseError(error).toLowerCase();
  return text.includes('requires-recent-login') || text.includes('recent authentication');
};

const getNativeCurrentUser = async () => {
  if (!isNativeFirebasePluginAvailable()) return null;
  const result = await FirebaseAuthentication.getCurrentUser();
  return result?.user || null;
};

const getNativeIdToken = async (forceRefresh = false) => {
  if (!isNativeFirebasePluginAvailable()) return '';
  const result = await FirebaseAuthentication.getIdToken({ forceRefresh });
  return result?.token ? String(result.token) : '';
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getNativeProviderIds = async () => {
  try {
    const nativeUser = await getNativeCurrentUser();
    const ids = Array.from(
      new Set((nativeUser?.providerData || []).map((p) => p?.providerId).filter(Boolean)),
    );
    return ids;
  } catch {
    return [];
  }
};

const getHydratedWebCurrentUser = async (targetProviderId = '') => {
  if (firebaseAuth?.currentUser) return firebaseAuth.currentUser;
  const nativeUser = await getNativeCurrentUser();
  if (!nativeUser) return firebaseAuth?.currentUser || null;

  // Give the web SDK a brief window to hydrate from native auth state.
  for (let i = 0; i < 10; i += 1) {
    if (firebaseAuth?.currentUser) return firebaseAuth.currentUser;
    await sleep(150);
  }

  // If still missing, re-trigger ONE native session the user *already* has so the Web SDK hydrates.
  // Never call signInWithGoogle() when linking Google to an Apple-only account — that starts a new
  // Google session. Never call signInWithApple() while targetProviderId is google.com — that hijacks
  // "Connect Google" with an Apple sheet. Native linking uses linkWithGoogle / linkWithApple instead.
  const providerIds = Array.from(new Set((nativeUser?.providerData || []).map((p) => p?.providerId).filter(Boolean)));
  const hasApple = providerIds.includes('apple.com');
  const hasGoogle = providerIds.includes('google.com');

  if (isNativeFirebasePluginAvailable()) {
    const wantGoogle = targetProviderId === 'google.com';
    const wantApple = targetProviderId === 'apple.com';

    if (wantGoogle) {
      if (hasGoogle && typeof FirebaseAuthentication.signInWithGoogle === 'function') {
        await FirebaseAuthentication.signInWithGoogle();
      }
    } else if (wantApple) {
      if (hasApple && typeof FirebaseAuthentication.signInWithApple === 'function') {
        await FirebaseAuthentication.signInWithApple();
      } else if (hasGoogle && typeof FirebaseAuthentication.signInWithGoogle === 'function') {
        await FirebaseAuthentication.signInWithGoogle();
      } else if (typeof FirebaseAuthentication.signInWithApple === 'function') {
        await FirebaseAuthentication.signInWithApple();
      }
    } else if (hasApple && typeof FirebaseAuthentication.signInWithApple === 'function') {
      await FirebaseAuthentication.signInWithApple();
    } else if (hasGoogle && typeof FirebaseAuthentication.signInWithGoogle === 'function') {
      await FirebaseAuthentication.signInWithGoogle();
    }
  }
  for (let i = 0; i < 12; i += 1) {
    if (firebaseAuth?.currentUser) return firebaseAuth.currentUser;
    await sleep(150);
  }
  return firebaseAuth?.currentUser || null;
};

export const signInWithGoogle = async () => {
  if (!firebaseAuth) {
    const missingText = missingConfigKeys.length ? ` Missing: ${missingConfigKeys.join(', ')}.` : '';
    throw new Error(`Firebase is not configured.${missingText}`);
  }

  const isNative = Capacitor.isNativePlatform();
  console.log('🔐 signInWithGoogle called, isNative:', isNative);
  
  try {
    if (!isNative) {
      // Popup sign-in is more reliable than redirect on many static hosts (redirect was returning with no session).
      console.log('🔐 Web: Google sign-in via popup');
      return signInWithPopup(firebaseAuth, googleProvider);
    }

    // Try native auth first
    const isPluginAvailable = Capacitor.isPluginAvailable('FirebaseAuthentication');
    console.log('🔐 FirebaseAuthentication plugin available:', isPluginAvailable);
    
    if (isPluginAvailable) {
      console.log('🔐 Attempting native Google sign-in...');
      const nativeResult = await FirebaseAuthentication.signInWithGoogle();
      const idToken = nativeResult?.credential?.idToken;
      const accessToken = nativeResult?.credential?.accessToken;
      if (!idToken) {
        throw new Error('Native Google sign-in returned no ID token. Verify iOS Google/Firebase setup.');
      }
      const credential = GoogleAuthProvider.credential(idToken, accessToken);
      return signInWithCredential(firebaseAuth, credential);
    }

    throw new Error('Google sign-in is unavailable: FirebaseAuthentication plugin is not available on this device build.');
  } catch (error) {
    console.error('🔐 signInWithGoogle error:', error);
    throw new Error(`Google sign-in failed: ${formatFirebaseError(error)}`);
  }
};

export const signInWithApple = async () => {
  if (!firebaseAuth) {
    const missingText = missingConfigKeys.length ? ` Missing: ${missingConfigKeys.join(', ')}.` : '';
    throw new Error(`Firebase is not configured.${missingText}`);
  }

  const isNative = Capacitor.isNativePlatform();
  try {
    if (!isNative) {
      if (shouldUseWebAppleRedirect()) {
        ensureWebFirebaseAuthContinuationPathIsRoot();
        return signInWithRedirect(firebaseAuth, appleProvider);
      }
      return signInWithPopup(firebaseAuth, appleProvider);
    }

    const isPluginAvailable = Capacitor.isPluginAvailable('FirebaseAuthentication');
    if (isPluginAvailable && typeof FirebaseAuthentication.signInWithApple === 'function') {
      const nativeResult = await FirebaseAuthentication.signInWithApple();
      // With skipNativeAuth=false, iOS may already complete Firebase auth natively.
      if (nativeResult?.user) {
        return nativeResult;
      }
      const idToken = nativeResult?.credential?.idToken || nativeResult?.credential?.identityToken;
      const rawNonce = nativeResult?.credential?.nonce || nativeResult?.credential?.rawNonce;
      if (!idToken) {
        throw new Error('Native Apple sign-in returned no ID token. Verify iOS Apple Sign-In setup.');
      }
      const credential = appleProvider.credential({ idToken, rawNonce });
      return signInWithCredential(firebaseAuth, credential);
    }

    throw new Error('Apple sign-in is unavailable: FirebaseAuthentication plugin is not available on this device build.');
  } catch (error) {
    throw new Error(`Apple sign-in failed: ${formatFirebaseError(error)}`);
  }
};

export const signInWithEmailPassword = async (email, password) => {
  if (!firebaseAuth) {
    const missingText = missingConfigKeys.length ? ` Missing: ${missingConfigKeys.join(', ')}.` : '';
    throw new Error(`Firebase is not configured.${missingText}`);
  }
  const safeEmail = String(email || '').trim();
  const safePassword = String(password || '');
  if (!safeEmail || !safePassword) {
    throw new Error('Email and password are required.');
  }
  try {
    if (
      isNativeFirebasePluginAvailable()
      && typeof FirebaseAuthentication.signInWithEmailAndPassword === 'function'
    ) {
      try {
        const nativeResult = await FirebaseAuthentication.signInWithEmailAndPassword({
          email: safeEmail,
          password: safePassword,
        });
        if (nativeResult?.user?.uid) {
          return nativeResult;
        }
      } catch (nativeError) {
        // Fall back to web SDK path below.
        console.warn('Native email/password sign-in failed, retrying with web SDK:', formatFirebaseError(nativeError));
      }
    }

    const TIMEOUT_MS = 12000;
    const result = await Promise.race([
      signInWithEmailAndPassword(firebaseAuth, safeEmail, safePassword),
      new Promise((_, reject) => {
        window.setTimeout(() => reject(new Error(`Email/password sign-in timed out after ${TIMEOUT_MS}ms`)), TIMEOUT_MS);
      }),
    ]);
    return result;
  } catch (error) {
    // If auth completed but the promise timed out/hung, return hydrated user anyway.
    const user = firebaseAuth?.currentUser;
    if (user?.uid) {
      return { user };
    }
    throw new Error(`Email/password sign-in failed: ${formatFirebaseError(error)}`);
  }
};

export const getLinkedProviderIds = () => {
  const user = firebaseAuth?.currentUser;
  if (!user) return [];
  return Array.from(new Set((user.providerData || []).map((p) => p?.providerId).filter(Boolean)));
};

export const getLinkedProviderIdsLive = async () => {
  const webIds = getLinkedProviderIds();
  const nativeIds = await getNativeProviderIds();
  return Array.from(new Set([...(webIds || []), ...(nativeIds || [])]));
};

export const linkCurrentUserWithGoogle = async () => {
  if (!firebaseAuth) {
    const missingText = missingConfigKeys.length ? ` Missing: ${missingConfigKeys.join(', ')}.` : '';
    throw new Error(`Firebase is not configured.${missingText}`);
  }

  const liveLinked = await getLinkedProviderIdsLive();
  if (liveLinked.includes('google.com')) {
    return firebaseAuth?.currentUser || null;
  }

  if (!Capacitor.isNativePlatform()) {
    const currentUser = await getHydratedWebCurrentUser('google.com');
    if (!currentUser) {
      throw new Error('No signed-in user found for linking. Please sign out and sign in again, then retry.');
    }
    try {
      await reload(currentUser);
    } catch {
      /* ignore */
    }
    return linkWithPopup(currentUser, googleProvider);
  }

  if (Capacitor.isPluginAvailable('FirebaseAuthentication') && typeof FirebaseAuthentication.linkWithGoogle === 'function') {
    await FirebaseAuthentication.linkWithGoogle();
    const webUser = firebaseAuth?.currentUser;
    if (webUser) {
      try {
        await reload(webUser);
      } catch {
        /* ignore */
      }
    }
    return webUser;
  }

  const currentUser = await getHydratedWebCurrentUser('');
  if (!currentUser) {
    throw new Error('No signed-in user found for linking. Please sign out and sign in again, then retry.');
  }
  try {
    await reload(currentUser);
  } catch {
    /* ignore */
  }
  if (Capacitor.isPluginAvailable('FirebaseAuthentication')) {
    const nativeResult = await FirebaseAuthentication.signInWithGoogle();
    const idToken = nativeResult?.credential?.idToken;
    const accessToken = nativeResult?.credential?.accessToken;
    if (!idToken) {
      throw new Error('Native Google link returned no ID token.');
    }
    const credential = GoogleAuthProvider.credential(idToken, accessToken);
    return linkWithCredential(currentUser, credential);
  }
  throw new Error('Google linking is unavailable on this device build.');
};

export const linkCurrentUserWithApple = async () => {
  if (!firebaseAuth) {
    const missingText = missingConfigKeys.length ? ` Missing: ${missingConfigKeys.join(', ')}.` : '';
    throw new Error(`Firebase is not configured.${missingText}`);
  }

  const liveLinked = await getLinkedProviderIdsLive();
  if (liveLinked.includes('apple.com')) {
    return firebaseAuth?.currentUser || null;
  }

  if (!Capacitor.isNativePlatform()) {
    const currentUser = await getHydratedWebCurrentUser('apple.com');
    if (!currentUser) {
      throw new Error('No signed-in user found for linking. Please sign out and sign in again, then retry.');
    }
    try {
      await reload(currentUser);
    } catch {
      /* ignore */
    }
    if (shouldUseWebAppleRedirect()) {
      ensureWebFirebaseAuthContinuationPathIsRoot();
      return linkWithRedirect(currentUser, appleProvider);
    }
    return linkWithPopup(currentUser, appleProvider);
  }

  if (Capacitor.isPluginAvailable('FirebaseAuthentication') && typeof FirebaseAuthentication.linkWithApple === 'function') {
    await FirebaseAuthentication.linkWithApple();
    const webUser = firebaseAuth?.currentUser;
    if (webUser) {
      try {
        await reload(webUser);
      } catch {
        /* ignore */
      }
    }
    return webUser;
  }

  const currentUser = await getHydratedWebCurrentUser('');
  if (!currentUser) {
    throw new Error('No signed-in user found for linking. Please sign out and sign in again, then retry.');
  }
  try {
    await reload(currentUser);
  } catch {
    /* ignore */
  }
  if (
    Capacitor.isPluginAvailable('FirebaseAuthentication')
    && typeof FirebaseAuthentication.signInWithApple === 'function'
  ) {
    const nativeResult = await FirebaseAuthentication.signInWithApple();
    const idToken = nativeResult?.credential?.idToken || nativeResult?.credential?.identityToken;
    const rawNonce = nativeResult?.credential?.nonce || nativeResult?.credential?.rawNonce;
    if (!idToken) {
      throw new Error('Native Apple link returned no ID token.');
    }
    const credential = appleProvider.credential({ idToken, rawNonce });
    return linkWithCredential(currentUser, credential);
  }
  throw new Error('Apple linking is unavailable on this device build.');
};

export const signOutFirebase = async () => {
  if (!firebaseAuth) return;
  if (Capacitor.isNativePlatform()) {
    try {
      await FirebaseAuthentication.signOut();
    } catch {
      // Ignore native sign-out failures and continue with web-layer sign-out.
    }
  }
  await signOut(firebaseAuth);
};

export const refreshRecentLoginForCurrentUser = async () => {
  const user = firebaseAuth?.currentUser;
  const webProviderIds = user
    ? Array.from(new Set((user.providerData || []).map((p) => p?.providerId).filter(Boolean)))
    : [];
  const nativeProviderIds = await getNativeProviderIds();
  const providerIds = Array.from(new Set([...webProviderIds, ...nativeProviderIds]));
  const prefersApple = providerIds.includes('apple.com');
  const prefersGoogle = providerIds.includes('google.com');

  if (Capacitor.isNativePlatform() && isNativeFirebasePluginAvailable()) {
    if (prefersApple && typeof FirebaseAuthentication.signInWithApple === 'function') {
      await FirebaseAuthentication.signInWithApple();
      return;
    }
    if ((prefersGoogle || !prefersApple) && typeof FirebaseAuthentication.signInWithGoogle === 'function') {
      await FirebaseAuthentication.signInWithGoogle();
      return;
    }
    throw new Error('Could not refresh sign-in. No supported native provider available.');
  }

  if (!user) {
    throw new Error('No signed-in user found.');
  }

  if (prefersApple) {
    if (shouldUseWebAppleRedirect()) {
      ensureWebFirebaseAuthContinuationPathIsRoot();
      await reauthenticateWithRedirect(user, appleProvider);
      return;
    }
    await reauthenticateWithPopup(user, appleProvider);
    return;
  }
  await reauthenticateWithPopup(user, googleProvider);
};

export const deleteFirebaseAccount = async () => {
  if (isNativeFirebasePluginAvailable() && typeof FirebaseAuthentication.deleteUser === 'function') {
    try {
      await FirebaseAuthentication.deleteUser();
      return;
    } catch (nativeError) {
      if (isRequiresRecentLoginError(nativeError)) {
        await refreshRecentLoginForCurrentUser();
        await FirebaseAuthentication.deleteUser();
        return;
      }
      // Fallback to web SDK delete below if native delete fails for other reasons.
      console.warn('⚠️ Native deleteUser failed:', formatFirebaseError(nativeError));
    }
  }

  const user = firebaseAuth?.currentUser;
  if (!user) {
    throw new Error('No signed-in web user found. Please sign out and sign in again, then retry delete.');
  }
  try {
    await deleteUser(user);
  } catch (webError) {
    if (isRequiresRecentLoginError(webError)) {
      await refreshRecentLoginForCurrentUser();
      const refreshedUser = firebaseAuth?.currentUser;
      if (!refreshedUser) throw new Error('Re-authentication completed but no signed-in user was found.');
      await deleteUser(refreshedUser);
      return;
    }
    throw new Error(formatFirebaseError(webError));
  }
};

export const subscribeToAuth = (callback) => {
  if (!firebaseAuth) {
    callback(null);
    return () => {};
  }

  const unsubscribeWeb = onAuthStateChanged(firebaseAuth, callback);

  let nativeAuthHandle = null;
  if (isNativeFirebasePluginAvailable()) {
    void getNativeCurrentUser()
      .then((nativeUser) => {
        // Native auth may already be signed in before the web auth observer updates.
        if (!firebaseAuth.currentUser && nativeUser) {
          callback(nativeUser);
        }
      })
      .catch((error) => {
        console.warn('⚠️ Native current user check failed:', formatFirebaseError(error));
      });

    void FirebaseAuthentication.addListener('authStateChange', async (change) => {
      let next = change?.user || null;
      // Native can emit a transient null while the web Firebase user is still valid (e.g. provider link).
      if (!next) {
        if (firebaseAuth?.currentUser) {
          // Keep web user as source of truth for providers native plugin does not manage
          // (e.g. web email/password in demo/review mode on iOS).
          next = firebaseAuth.currentUser;
        } else {
          try {
            const snap = await FirebaseAuthentication.getCurrentUser();
            if (snap?.user) next = snap.user;
          } catch {
            /* ignore */
          }
        }
      }
      callback(next);
    })
      .then((handle) => {
        nativeAuthHandle = handle;
      })
      .catch((error) => {
        console.warn('⚠️ Native auth listener setup failed:', formatFirebaseError(error));
      });
  }

  return () => {
    unsubscribeWeb();
    if (nativeAuthHandle) {
      void nativeAuthHandle.remove();
    }
  };
};

export const getCurrentFirebaseUser = () => firebaseAuth?.currentUser || null;

/** True when the user is signed in on web SDK or native Capacitor Firebase (web `currentUser` can lag on iOS). */
export const isClientFirebaseSignedIn = async () => {
  if (firebaseAuth?.currentUser?.uid) return true;
  if (!isNativeFirebasePluginAvailable()) return false;
  try {
    const nu = await getNativeCurrentUser();
    return Boolean(nu?.uid);
  } catch {
    return false;
  }
};

export const getCurrentFirebaseIdToken = async () => {
  const user = getCurrentFirebaseUser();
  if (user) {
    return user.getIdToken();
  }

  // On iOS, native auth can complete before the web SDK session is hydrated.
  try {
    const nativeToken = await getNativeIdToken(false);
    return nativeToken;
  } catch {
    return '';
  }
};
